import React, {
  createContext, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import ExhibitLightbox from '../ExhibitLightbox';

// Exhibit images arrive with inconsistent source dimensions and inconsistent
// internal scale, so no single inline max-height reads well for every image.
// PaperImage instead stays small/consistent and opens ExhibitLightbox (via
// context, since PaperImage is also rendered inside TmuaPearsonSolutionModal
// across a component-file boundary) so the user can zoom to whatever size
// makes the exhibit legible.
const ExhibitLightboxContext = createContext(null);

/**
 * Provides ExhibitLightboxContext ({ openLightbox, lightboxOpenRef }) and
 * draws the lightbox itself, for everything inside it. QuizDisplaySection
 * wraps the player in it; the staff paper review wraps itself in it.
 */
export const ExhibitLightboxProvider = ({ children }) => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const openLightbox = useCallback((image) => setLightboxImage(image), []);
  const closeLightbox = useCallback(() => setLightboxImage(null), []);
  // Read at keydown time (via ref, not the memoized state) so exam shortcuts
  // (n/e/f, digits, arrows) can't fire behind an open lightbox. Derived from
  // lightboxImage rather than set inside openLightbox/closeLightbox so there's
  // one source of truth even if something else ever clears lightboxImage.
  const lightboxOpenRef = useRef(false);
  useEffect(() => {
    lightboxOpenRef.current = Boolean(lightboxImage);
  }, [lightboxImage]);
  const contextValue = useMemo(
    () => ({ openLightbox, lightboxOpenRef }),
    [openLightbox]
  );
  return (
    <ExhibitLightboxContext.Provider value={contextValue}>
      {children}
      {lightboxImage && (
        <ExhibitLightbox
          url={lightboxImage.url}
          alt={lightboxImage.alt}
          onClose={closeLightbox}
        />
      )}
    </ExhibitLightboxContext.Provider>
  );
};

export { ExhibitLightboxContext };
